// The page shown when the globe can't run here (FR-060) or the visitor cap is
// hit (FR-063). renderFallback is pure so it can be tested without a DOM.
import { GPU_FAILURE_MESSAGES, type GpuFailureReason } from '@client/gpu/device.ts';

export type FallbackReason = GpuFailureReason | 'capped' | 'paused';

export const FALLBACK_REASONS = [
  'no-webgpu',
  'no-adapter',
  'limits',
  'device-failed',
  'device-lost',
  'capped',
  'paused',
] as const satisfies readonly FallbackReason[];

// Assumed: neither URL is written down in the spec, plan or research yet.
export const SITE_LINKS = {
  rnd: 'https://fortitude-omnis.group/rd/',
  company: 'https://fortitude-omnis.group/',
} as const;

export const FALLBACK_VIDEO = { src: '/video/fallback.mp4', poster: '/video/poster.webp' } as const;

const HEADINGS: Readonly<Record<FallbackReason, string>> = {
  'no-webgpu': 'The live globe can’t run in this browser',
  'no-adapter': 'The live globe can’t run on this device',
  limits: 'The live globe can’t run on this device',
  'device-failed': 'The live globe couldn’t start',
  'device-lost': 'The live globe stopped',
  capped: 'The live globe is full right now',
  paused: 'The live globe is paused',
};

const MESSAGES: Readonly<Record<FallbackReason, string>> = {
  ...GPU_FAILURE_MESSAGES,
  capped: 'Lots of people are watching at the moment. Please try again in a few minutes.',
  paused: 'The live globe is switched off for a little while. Please try again shortly.',
};

const NEXT_STEP: Readonly<Record<FallbackReason, string>> = {
  'no-webgpu': 'It works in recent Chrome, Edge and Safari, and in Firefox on Windows.',
  'no-adapter': 'Turning on hardware acceleration in your browser settings may help.',
  limits: 'A newer phone, tablet or computer should run it.',
  'device-failed': 'Reloading the page may help.',
  'device-lost': 'Reloading the page should bring it back.',
  capped: 'In the meantime, here is a recording of the globe.',
  paused: 'In the meantime, here is a recording of the globe.',
};

const VIDEO_TEXT =
  'A recording of the globe: the Earth turning from night into day, with satellites and debris, the aurora, the wind, lightning and wildfires.';

export function isFallbackReason(value: unknown): value is FallbackReason {
  return typeof value === 'string' && (FALLBACK_REASONS as readonly string[]).includes(value);
}

// Reads `?reason=` from a query string. Anything unknown shows the browser message.
export function parseFallbackReason(search: string): FallbackReason {
  const value = new URLSearchParams(search).get('reason');
  return isFallbackReason(value) ? value : 'no-webgpu';
}

const ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

export interface FallbackOptions {
  readonly links?: { readonly rnd: string; readonly company: string };
  readonly video?: { readonly src: string; readonly poster: string };
  /** Replaces the reason's message (the owner's note from status.json). Set with textContent. */
  readonly message?: string;
}

export function fallbackCopy(reason: FallbackReason): { heading: string; message: string; nextStep: string } {
  return { heading: HEADINGS[reason], message: MESSAGES[reason], nextStep: NEXT_STEP[reason] };
}

export function renderFallback(reason: FallbackReason, options: FallbackOptions = {}): string {
  const links = options.links ?? SITE_LINKS;
  const video = options.video ?? FALLBACK_VIDEO;
  const copy = fallbackCopy(isFallbackReason(reason) ? reason : 'no-webgpu');
  const e = escapeHtml;
  return `<section class="fallback" data-reason="${e(reason)}" aria-labelledby="fallback-title">
  <h1 id="fallback-title">${e(copy.heading)}</h1>
  <p class="fallback-message">${e(copy.message)}</p>
  <p class="fallback-next">${e(copy.nextStep)}</p>
  <figure class="fallback-video">
    <video controls muted loop playsinline preload="none" poster="${e(video.poster)}" aria-describedby="fallback-video-text">
      <source src="${e(video.src)}" type="video/mp4" />
    </video>
    <figcaption id="fallback-video-text">${e(VIDEO_TEXT)}</figcaption>
  </figure>
  <h2>Find out more</h2>
  <ul class="fallback-links">
    <li><a href="${e(links.rnd)}">How the globe was built, on the R&amp;D pages</a></li>
    <li><a href="${e(links.company)}">The Fortitude Omnis website</a></li>
  </ul>
</section>`;
}

// What the poster is replaced with if the video file is missing.
export function renderPosterImage(poster: string): string {
  return `<img src="${escapeHtml(poster)}" alt="${escapeHtml(VIDEO_TEXT)}" />`;
}

// Mounts the page and copes with the video or poster not existing yet (T114):
// no video falls back to the poster, no poster leaves the caption on its own.
export function mountFallback(root: Element, reason: FallbackReason, options: FallbackOptions = {}): void {
  const video = options.video ?? FALLBACK_VIDEO;
  root.innerHTML = renderFallback(reason, options);
  if (options.message) {
    const note = root.querySelector('.fallback-message');
    if (note) note.textContent = options.message;
  }
  const player = root.querySelector('video');
  const source = player?.querySelector('source');
  if (!player || !source) return;

  source.addEventListener('error', () => {
    const holder = root.ownerDocument.createElement('div');
    holder.innerHTML = renderPosterImage(video.poster);
    const img = holder.firstElementChild;
    if (!img) return;
    img.addEventListener('error', () => img.remove());
    player.replaceWith(img);
  });

  const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? true;
  if (!reduceMotion) {
    player.preload = 'auto';
    void player.play().catch(() => undefined);
  }
}
